import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Banknote, ChevronDown, ChevronUp, Coins, FileSignature, Gift, Info, PiggyBank, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { HostBottomNav } from '../../components/HostBottomNav';
import NotificationBellButton from '../../components/NotificationBellButton';
import { hostCardStyle } from '../../components/HostSettingsCard';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import WelcomeTourSheet from '../../components/WelcomeTourSheet';
import PageInfoButton from '../../components/PageInfoButton';
import { hostEarningsTourSlides } from '../../components/welcomeTourSlides';
import { hasSeenWelcomeTour, markWelcomeTourSeen } from '../../utils/welcomeTourState';

// =============================================================================
// FINANCIAL PROTOCOL — READ BEFORE EDITING THIS FILE
// -----------------------------------------------------------------------------
// Host-facing earnings UI. The exact % of the future tier MUST stay hidden
// from hosts — show only "?%" + a motivational message. Never reveal the
// ceiling. Any change to a fee, percentage, commission rate, or flat-fee
// constant MUST be accompanied by a synchronized update to BOTH:
//   - /app/memory/FINANCIAL_PROTOCOL.md         (source-of-truth doc)
//   - /app/backend/tests/test_financial_protocol.py  (regression test suite)
// When in doubt, ASK THE USER. Do not guess. (See FINANCIAL_PROTOCOL.md §4.)
// =============================================================================

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

const cardStyle = {
  ...hostCardStyle,
  width: '100%',
  padding: '16px 16px 18px',
  display: 'grid',
  gap: 10,
};

const formatEuro = (value) => `${new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0))}€`;

const BreakdownRow = ({ label, value, testId }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
    <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>{label}</span>
    <span data-testid={testId} style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{value}</span>
  </div>
);

const HighlightBreakdownRow = ({ label, value, testId }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
    <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>{label}</span>
    <span data-testid={testId} style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: '#04A628' }}>{value}</span>
  </div>
);

// Visual 3-tier progression chip strip. Visible to ALL hosts so the upward
// path is always obvious — but the third tier's exact % is intentionally
// HIDDEN (shown as "?%") to keep the ceiling invisible and motivation high.
// Source of truth: /app/memory/FINANCIAL_PROTOCOL.md.
const HOST_TIER_STRIP = [
  { id: 'base',     label: '0.5%', status: 'Base',         minRate: 0.0, maxRate: 0.99 },
  { id: 'unlocked', label: '2%',   status: 'Unlocked',     minRate: 1.0, maxRate: 2.99 },
  { id: 'future',   label: '?%',   status: 'Coming soon',  minRate: 3.0, maxRate: 99.0, comingSoon: true },
];

const CommissionTierStrip = ({ currentRate }) => {
  const rate = Number(currentRate || 0);
  return (
    <div data-testid="host-earnings-tier-strip" style={{ display: 'flex', alignItems: 'stretch', gap: 6, marginTop: 2 }}>
      <style>{`
        @keyframes hostTierFutureFloat { 0%,100% { transform: translateY(0); opacity: .85; } 50% { transform: translateY(-3px); opacity: 1; } }
        @keyframes hostTierFutureSparkle { 0%,100% { opacity: .35; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1.15); } }
      `}</style>
      {HOST_TIER_STRIP.map((tier, index) => {
        const isCurrent = rate >= tier.minRate && rate <= tier.maxRate && !tier.comingSoon;
        return (
          <React.Fragment key={tier.id}>
            <div
              data-testid={`host-earnings-tier-chip-${tier.id}`}
              style={{
                flex: 1,
                borderRadius: 12,
                padding: '8px 10px',
                background: isCurrent ? 'var(--status-success-surface)' : tier.comingSoon ? 'var(--status-warning-surface)' : 'var(--surface-2)',
                border: isCurrent ? '2px solid #04A628' : tier.comingSoon ? '1px dashed #E0A33D' : '1px solid var(--hairline)',
                display: 'grid',
                gap: 2,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {tier.comingSoon ? (
                <>
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 6,
                      fontSize: 10,
                      color: '#E0A33D',
                      animation: 'hostTierFutureSparkle 2.4s ease-in-out infinite',
                      pointerEvents: 'none',
                    }}
                  >
                    ✦
                  </span>
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      bottom: 4,
                      left: 6,
                      fontSize: 9,
                      color: '#E0A33D',
                      animation: 'hostTierFutureSparkle 3.1s ease-in-out infinite 0.6s',
                      pointerEvents: 'none',
                    }}
                  >
                    ✦
                  </span>
                </>
              ) : null}
              <span
                style={{
                  fontFamily: font,
                  fontSize: 18,
                  fontWeight: 700,
                  color: tier.comingSoon ? 'var(--status-warning-text)' : 'var(--text)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  animation: tier.comingSoon ? 'hostTierFutureFloat 2.6s ease-in-out infinite' : undefined,
                }}
              >
                {tier.comingSoon ? <TrendingUp size={14} color="var(--status-warning-text)" strokeWidth={2.6} /> : null}
                {tier.label}
              </span>
              <span style={{ fontFamily: font, fontSize: 11, fontWeight: 700, color: tier.comingSoon ? 'var(--status-warning-text)' : isCurrent ? '#04A628' : 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {isCurrent ? 'You are here' : tier.status}
              </span>
            </div>
            {index < HOST_TIER_STRIP.length - 1 ? (
              <div style={{ display: 'flex', alignItems: 'center', color: 'var(--text-3)', fontWeight: 700, fontSize: 16 }}>→</div>
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const PoolProgressSection = ({
  label,
  value,
  percent,
  valueTestId,
  barTestId,
  fillTestId,
  trackColor,
  fillBackground,
}) => (
  <div style={{ display: 'grid', gap: 8 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
      <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>{label}</span>
      <span data-testid={valueTestId} style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{value}</span>
    </div>
    <div data-testid={barTestId} style={{ width: '100%', height: 10, borderRadius: 9999, background: trackColor, overflow: 'hidden' }}>
      <div
        data-testid={fillTestId}
        style={{
          width: `${percent}%`,
          height: '100%',
          borderRadius: 9999,
          background: fillBackground,
          transition: 'width 260ms ease',
        }}
      />
    </div>
  </div>
);

const PayoutControlSection = ({ loading }) => {
  const navigate = useNavigate();
  const [eligibility, setEligibility] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const fetchEligibility = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`${API}/host/payout-eligibility`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEligibility(res.data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (!loading) fetchEligibility();
  }, [loading, fetchEligibility]);

  // Re-fetch the moment the user finishes signing so the payout-hold banner
  // clears without waiting for a reload.
  useEffect(() => {
    const handler = () => fetchEligibility();
    window.addEventListener('agreement:accepted', handler);
    return () => window.removeEventListener('agreement:accepted', handler);
  }, [fetchEligibility]);

  const handleToggleAutoPayout = async () => {
    if (!eligibility || toggling) return;
    setToggling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/host/payout-preferences`, {
        enabled: !eligibility.auto_payout,
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setEligibility((prev) => ({ ...prev, auto_payout: !prev.auto_payout }));
      toast.success(eligibility.auto_payout ? 'Automatic payouts paused' : 'Automatic payouts enabled');
    } catch {
      toast.error('Failed to update preference');
    } finally {
      setToggling(false);
    }
  };

  const handleRequestPayout = async () => {
    if (requesting || !eligibility?.can_request_payout) return;
    setRequesting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/host/request-payout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data?.message || 'Payout initiated!');
      fetchEligibility();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payout request failed');
    } finally {
      setRequesting(false);
    }
  };

  if (!eligibility) return null;

  const { has_vat, auto_payout, can_request_payout, block_reasons, block_reason_text, next_payout_date, agreement_gate } = eligibility;
  const showVatWarning = !has_vat;
  const hasBlockReasons = block_reasons && block_reasons.length > 0;
  const hasAgreementHold = Array.isArray(block_reasons) && block_reasons.includes('pending_agreement_reaccept');
  const heldAmount = Number(agreement_gate?.held_amount_eur || 0);
  const heldAmountLabel = heldAmount > 0 ? formatEuro(heldAmount) : 'your';

  const openAgreementReview = () => {
    try {
      window.dispatchEvent(new CustomEvent('agreement:open-reaccept', { detail: { kind: 'host' } }));
    } catch {
      // Fallback: navigate to My Agreements page.
      navigate('/my-agreements');
    }
  };

  const nextPayoutLabel = next_payout_date
    ? new Date(next_payout_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  return (
    <div data-testid="host-earnings-payout-control" style={{ display: 'grid', gap: 14, paddingTop: 4, borderTop: '1px solid var(--hairline)' }}>
      {hasAgreementHold && (
        <div
          data-testid="host-earnings-agreement-hold-banner"
          style={{
            display: 'grid', gap: 10, padding: '14px 16px',
            borderRadius: 16, background: 'rgba(120, 88, 214, 0.10)',
            border: '1.5px solid rgba(120, 88, 214, 0.45)',
          }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <FileSignature size={22} color="var(--accent-purple)" strokeWidth={2.1} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontFamily: font, fontSize: 16, fontWeight: 800, color: 'var(--accent-purple)', margin: 0 }}>
                Payout on hold
              </p>
              <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text)', margin: '4px 0 0', lineHeight: 1.45, fontWeight: 600 }}>
                Sign the updated Host Listing Agreement to release {heldAmountLabel} payout.
              </p>
              {agreement_gate?.changelog_note ? (
                <p style={{ fontFamily: font, fontSize: 12, color: 'var(--text-2)', margin: '4px 0 0', lineHeight: 1.4 }}>
                  {agreement_gate.changelog_note}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            data-testid="host-earnings-agreement-review-button"
            onClick={openAgreementReview}
            style={{
              width: '100%', padding: '12px 16px', borderRadius: 9999,
              border: 'none', background: 'var(--accent-purple)',
              color: '#FFF', fontFamily: font, fontSize: 16, fontWeight: 800,
              cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 8,
            }}
          >
            <FileSignature size={18} strokeWidth={2.3} />
            Review &amp; sign
          </button>
        </div>
      )}

      {showVatWarning && (
        <button
          type="button"
          data-testid="host-earnings-vat-warning"
          onClick={() => navigate('/host/settings/vat')}
          style={{
            display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px',
            borderRadius: 14, border: '1.5px solid #F59E0B', background: 'none',
            cursor: 'pointer', textAlign: 'left', width: '100%',
          }}
        >
          <AlertTriangle size={20} color="#F59E0B" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <p style={{ fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
              VAT / Tax ID required
            </p>
            <p style={{ fontFamily: font, fontSize: 12, color: 'var(--text-2)', margin: '3px 0 0', lineHeight: 1.4 }}>
              To receive payouts, add your VAT or Tax ID. Tap here.
            </p>
          </div>
        </button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
            Automatic monthly payout
          </p>
          <p style={{ fontFamily: font, fontSize: 12, color: 'var(--text-2)', margin: '3px 0 0', lineHeight: 1.3 }}>
            {auto_payout
              ? `Payouts are sent automatically on the 1st of each month.`
              : `Automatic payouts are paused. Use the button below to request manually.`}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={auto_payout}
          aria-label="Automatic monthly payout"
          data-testid="host-earnings-auto-payout-toggle"
          onClick={handleToggleAutoPayout}
          disabled={toggling}
          style={{
            width: 54, height: 30, borderRadius: 999,
            border: '1px solid rgba(112, 63, 246, 0.1)',
            backgroundColor: auto_payout ? 'var(--accent-purple)' : 'var(--accent-surface)',
            padding: 3, cursor: toggling ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center',
            justifyContent: auto_payout ? 'flex-end' : 'flex-start',
            transition: 'background-color 180ms ease',
            flexShrink: 0, opacity: toggling ? 0.6 : 1,
          }}
        >
          <span style={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }} />
        </button>
      </div>

      {!auto_payout && (
        <>
          <button
            type="button"
            data-testid="host-earnings-request-payout-button"
            onClick={handleRequestPayout}
            disabled={!can_request_payout || requesting}
            style={{
              width: '100%', padding: '14px 16px', borderRadius: 9999,
              fontFamily: font, fontSize: 17, fontWeight: 700,
              color: can_request_payout ? '#fff' : 'var(--text-2)',
              background: can_request_payout ? '#04A628' : 'var(--surface-2)',
              border: can_request_payout ? 'none' : '1px solid var(--card-border)',
              cursor: can_request_payout && !requesting ? 'pointer' : 'default',
              opacity: requesting ? 0.6 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'background-color 180ms ease, opacity 180ms ease',
            }}
          >
            <Banknote size={20} />
            {requesting ? 'Processing...' : 'Receive payout'}
          </button>

          {hasBlockReasons && !can_request_payout && (
            <div data-testid="host-earnings-payout-block-reason" style={{
              display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px',
              borderRadius: 12, background: 'var(--surface-2)',
            }}>
              <Info size={16} color="var(--text-2)" style={{ flexShrink: 0, marginTop: 2 }} />
              <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-2)', margin: 0, lineHeight: 1.4 }}>
                {block_reason_text || 'Payout not available right now.'}
              </p>
            </div>
          )}

        </>
      )}

      {auto_payout && nextPayoutLabel && (
        <p style={{ fontFamily: font, fontSize: 12, color: 'var(--text-2)', margin: 0 }}>
          Next payout: {nextPayoutLabel}
        </p>
      )}
    </div>
  );
};

export default function HostEarningsPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingExpanded, setUpcomingExpanded] = useState(false);
  const [pendingExpanded, setPendingExpanded] = useState(false);
  const [pageTourOpen, setPageTourOpen] = useState(false);

  useEffect(() => {
    if (!hasSeenWelcomeTour('host-page-earnings')) {
      setPageTourOpen(true);
    }
  }, []);

  const handleClosePageTour = () => {
    setPageTourOpen(false);
    markWelcomeTourSeen('host-page-earnings');
  };

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/host/earnings/overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOverview(response.data);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Unable to load earnings right now');
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, []);

  const rateActive = Number(overview?.commission_rate || 0) >= 2;
  const currentMonthActiveGuestCount = Number(overview?.current_month_active_guest_count ?? overview?.current_month_guest_count ?? 0);
  const currentMonthQualifiedGuestCount = Number(overview?.current_month_qualified_guest_count || 0);
  const guestRewardTarget = Number(overview?.guest_reward_target || 7);
  const qualifiedGuestTarget = Number(overview?.qualified_guest_target || 4);
  const currentMonthTransactionCount = Number(overview?.current_month_transaction_count || 0);
  const transactionsTarget = Number(overview?.transactions_target || 10);
  const nearMissReason = overview?.near_miss_reason || '';
  const activeGuestProgressPercent = loading
    ? 0
    : Math.min(100, Math.max(0, ((currentMonthActiveGuestCount / guestRewardTarget) * 100) || 0));
  const qualifiedGuestProgressPercent = loading
    ? 0
    : Math.min(100, Math.max(0, ((currentMonthQualifiedGuestCount / qualifiedGuestTarget) * 100) || 0));
  const transactionsProgressPercent = loading
    ? 0
    : Math.min(100, Math.max(0, ((currentMonthTransactionCount / transactionsTarget) * 100) || 0));
  const upcomingStays = overview?.upcoming_stays || [];
  const hasUpcomingStays = !loading && Number(overview?.upcoming_stays_total || 0) > 0 && upcomingStays.length > 0;

  return (
    <div className="app-shell" data-testid="host-earnings-page" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <PageInfoButton
          onClick={() => setPageTourOpen(true)}
          testId="host-earnings-page-info-button"
        />
        <NotificationBellButton buttonTestId="host-earnings-bell-button" popupTestId="host-earnings-notification-popup" />
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, paddingTop: 0 }}>
        <img src="/allure-logo.png" alt="Allure" style={{ width: 94, height: 82, objectFit: 'contain' }} />

        <div data-testid="host-earnings-total-card" style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 58, height: 58, borderRadius: '50%', background: 'var(--status-success-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingUp size={28} color="#04A628" />
            </div>
            <div>
              <p style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Total host earnings</p>
              <p data-testid="host-earnings-total-amount" style={{ fontFamily: font, fontSize: 26, fontWeight: 700, color: '#04A628', margin: '4px 0 0' }}>
                {loading ? '...' : formatEuro(overview?.paid_out_total)}
              </p>
            </div>
          </div>
          {rateActive ? <BreakdownRow label="Earning rate" testId="host-earnings-unlocked-rate" value={loading ? '...' : '2%'} /> : null}

          <button
            type="button"
            data-testid="host-earnings-pending-balance-toggle"
            onClick={() => setPendingExpanded((prev) => !prev)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', width: '100%' }}
          >
            <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>Pending balance</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span data-testid="host-earnings-pending-balance" style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
                {loading ? '...' : formatEuro(overview?.pending_balance)}
              </span>
              {pendingExpanded ? <ChevronUp size={16} color="var(--text-2)" /> : <ChevronDown size={16} color="var(--text-2)" />}
            </span>
          </button>

          {pendingExpanded && (
            <div data-testid="host-earnings-pending-balance-breakdown" style={{ display: 'grid', gap: 8, paddingLeft: 12, marginLeft: 2, borderLeft: '2px solid var(--hairline)' }}>
              <BreakdownRow label="Commission" testId="host-earnings-monthly-commission" value={loading ? '...' : formatEuro(overview?.pending_commission)} />
              <BreakdownRow label="Host tips" testId="host-earnings-monthly-tips" value={loading ? '...' : formatEuro(overview?.pending_host_tips)} />
              <BreakdownRow label="Pool reward" testId="host-earnings-monthly-rewards" value={loading ? '...' : formatEuro(overview?.pending_pool_reward)} />
            </div>
          )}

          <PayoutControlSection loading={loading} />

          <button
            type="button"
            data-testid="host-earnings-view-payouts-button"
            onClick={() => navigate('/host/settings/payouts/history')}
            style={{ marginTop: 2, borderRadius: 14, padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--card-border)', color: 'var(--text)', cursor: 'pointer', fontFamily: font, fontSize: 18, fontWeight: 700 }}
          >
            View payout ledger
          </button>
        </div>

        <div data-testid="host-earnings-pool-card" style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 16, background: 'var(--status-warning-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Gift size={24} color="#B57B00" />
              </div>
              <div>
                <p style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Monthly host pool</p>
                <p data-testid="host-earnings-pool-total" style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: '#04A628', margin: '4px 0 0' }}>
                  {loading ? '...' : formatEuro(overview?.current_month_pool_total)}
                </p>
              </div>
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="Host pool info"
                  data-testid="host-earnings-pool-info-button"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    border: 'none',
                    background: 'var(--status-info-surface)',
                    color: 'var(--status-info-text)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  <Info size={18} strokeWidth={2.3} />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={12} data-testid="host-earnings-pool-info-popover" className="border-0 bg-transparent p-0 shadow-none">
                <div style={{ position: 'relative', paddingTop: 8 }}>
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 18,
                      width: 18,
                      height: 18,
                      background: 'var(--surface)',
                      borderTop: '1px solid var(--card-border)',
                      borderLeft: '1px solid var(--card-border)',
                      transform: 'rotate(45deg)',
                      boxShadow: 'var(--premium-shadow-nested)',
                    }}
                  />
                  <div
                    style={{
                      background: 'var(--surface)',
                      border: '1px solid var(--card-border)',
                      borderRadius: 24,
                      boxShadow: 'var(--premium-shadow-main)',
                      padding: '16px 18px',
                    }}
                  >
                    <p data-testid="host-earnings-pool-info-text" style={{ fontFamily: font, fontSize: 16, color: 'var(--text)', lineHeight: 1.45, margin: 0 }}>
                      Invite 7+ guests in a month (with 4+ spending at a partner venue),<br />
                      <strong>OR</strong> have your guests complete 10+ transactions total.<br />
                      Either path unlocks your share of the host reward pool.
                    </p>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <BreakdownRow label="Eligible hosts" testId="host-earnings-eligible-host-count" value={loading ? '...' : `${overview?.current_month_eligible_host_count || 0}`} />
          <PoolProgressSection
            label="Guests this month"
            value={loading ? '...' : `${currentMonthActiveGuestCount}/${guestRewardTarget}`}
            valueTestId="host-earnings-monthly-progress"
            barTestId="host-earnings-pool-progress-bar"
            fillTestId="host-earnings-pool-progress-fill"
            percent={activeGuestProgressPercent}
            trackColor="var(--status-success-surface)"
            fillBackground="linear-gradient(90deg, #04A628 0%, #1CCB55 100%)"
          />
          <PoolProgressSection
            label="Guests qualified"
            value={loading ? '...' : `${currentMonthQualifiedGuestCount}/${qualifiedGuestTarget}`}
            valueTestId="host-earnings-qualified-progress"
            barTestId="host-earnings-qualified-progress-bar"
            fillTestId="host-earnings-qualified-progress-fill"
            percent={qualifiedGuestProgressPercent}
            trackColor="var(--status-info-surface)"
            fillBackground="linear-gradient(90deg, #2F79EA 0%, #54A4FF 100%)"
          />

          {/* Divider: "OR" between Track A (breadth) and Track B (volume) */}
          <div data-testid="host-earnings-track-divider" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 0' }}>
            <span style={{ flex: 1, height: 1, background: 'var(--hairline)' }} />
            <span style={{ fontFamily: font, fontSize: 13, fontWeight: 700, color: 'var(--text-3)', letterSpacing: 1.2 }}>OR</span>
            <span style={{ flex: 1, height: 1, background: 'var(--hairline)' }} />
          </div>

          <PoolProgressSection
            label="Total transactions"
            value={loading ? '...' : `${currentMonthTransactionCount}/${transactionsTarget}`}
            valueTestId="host-earnings-transactions-progress"
            barTestId="host-earnings-transactions-progress-bar"
            fillTestId="host-earnings-transactions-progress-fill"
            percent={transactionsProgressPercent}
            trackColor="var(--status-warning-surface)"
            fillBackground="linear-gradient(90deg, #E07A00 0%, #FFAA3F 100%)"
          />

          {/* Near-miss explainer — only shown when host hit a track target
              but their guests didn't spend enough (≥ €50) to qualify. */}
          {!loading && nearMissReason ? (
            <div
              data-testid="host-earnings-near-miss-banner"
              style={{
                borderRadius: 14,
                padding: '12px 14px',
                background: 'var(--status-warning-surface)',
                border: '1px solid #F5C147',
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
              }}
            >
              <Info size={18} color="var(--status-warning-text)" style={{ flexShrink: 0, marginTop: 2 }} />
              <p style={{ fontFamily: font, fontSize: 14, color: 'var(--status-warning-text)', margin: 0, lineHeight: 1.45 }}>
                {nearMissReason}
              </p>
            </div>
          ) : null}
          {loading ? (
            <BreakdownRow label="Estimated share" testId="host-earnings-estimated-pool-share" value="..." />
          ) : overview?.qualifies_for_monthly_pool ? (
            <HighlightBreakdownRow label="Estimated share" testId="host-earnings-estimated-pool-share" value={formatEuro(overview?.current_month_estimated_pool_share)} />
          ) : null}

          {hasUpcomingStays ? (
            <div style={{ display: 'grid', gap: upcomingExpanded ? 10 : 0, paddingTop: 2 }}>
              <button
                type="button"
                data-testid="host-earnings-upcoming-toggle"
                onClick={() => setUpcomingExpanded((prev) => !prev)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  cursor: 'pointer',
                }}
              >
                <span data-testid="host-earnings-upcoming-summary" style={{ fontFamily: font, fontSize: 15, color: '#2F79EA', fontWeight: 700 }}>
                  Upcoming stays · {overview?.upcoming_stays_total || 0}
                </span>
                {upcomingExpanded ? <ChevronUp size={18} color="#2F79EA" /> : <ChevronDown size={18} color="#2F79EA" />}
              </button>

              {upcomingExpanded ? (
                <div data-testid="host-earnings-upcoming-list" style={{ display: 'grid', gap: 8 }}>
                  {upcomingStays.map((month) => (
                    <div
                      key={month.month_key}
                      data-testid={`host-earnings-upcoming-month-${month.month_key}`}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingTop: 2 }}
                    >
                      <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>{month.month_label}</span>
                      <span style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
                        {month.active_guest_count}/{month.guest_target}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {!rateActive ? (
          <div data-testid="host-earnings-rate-card" style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 16, background: 'var(--status-info-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PiggyBank size={24} color="var(--status-info-text)" />
              </div>
              <div>
                <p style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Commission rate</p>
                <p data-testid="host-earnings-rate-value" style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '4px 0 0' }}>
                  {loading ? '...' : `${overview?.commission_rate ?? 0.5}%`}
                </p>
              </div>
            </div>
            <CommissionTierStrip currentRate={Number(overview?.commission_rate || 0.5)} />
            <BreakdownRow label="Valid invites sent" testId="host-earnings-referral-progress" value={loading ? '...' : `${overview?.active_referred_hosts ?? 0}/${overview?.referral_target ?? 2}`} />
            <BreakdownRow label="Joined Allure" testId="host-earnings-joined-invites-progress" value={loading ? '...' : `${overview?.joined_invited_hosts ?? 0}/${overview?.joined_invite_target ?? 1}`} />
            <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: 0 }}>Just 2 minutes now — you can send 1 invite every 48 hours</p>
            <button
              type="button"
              data-testid="host-unlock-higher-earnings-button"
              onClick={() => navigate('/host/referrals')}
              style={{ border: 'none', borderRadius: 9999, padding: '12px 20px', background: '#04A628', color: '#FFF', cursor: 'pointer', fontFamily: font, fontSize: 20, fontWeight: 700, boxShadow: '0 10px 24px rgba(4, 166, 40, 0.22)' }}
            >
              Unlock 2%
            </button>
          </div>
        ) : (
          <div data-testid="host-earnings-rate-card-active" style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 16, background: 'var(--status-success-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PiggyBank size={24} color="#04A628" />
              </div>
              <div>
                <p style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Commission rate</p>
                <p data-testid="host-earnings-rate-value" style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: '#04A628', margin: '4px 0 0' }}>
                  {loading ? '...' : `${overview?.commission_rate ?? 2}%`}
                </p>
              </div>
            </div>
            <CommissionTierStrip currentRate={Number(overview?.commission_rate || 2)} />
            <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: 0 }}>
              You{"'"}ve unlocked the 2% tier — nice work. The next tier <strong>won{"'"}t open right away</strong>: we{"'"}re growing Allure first, and once the network reaches the right scale we{"'"}ll unlock the next step. You{"'"}ll be among the first to know.
            </p>
          </div>
        )}

        <div data-testid="host-earnings-recent-tips-card" style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--status-info-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Coins size={20} color="var(--status-info-text)" />
            </div>
            <p style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Recent host tips</p>
          </div>
          {(overview?.recent_tips || []).length === 0 ? (
            <p style={{ fontFamily: font, fontSize: 15, color: 'var(--text-2)', margin: 0 }}>No host tips yet.</p>
          ) : (
            overview.recent_tips.map((tip) => (
              <div key={`${tip.id}-${tip.tipped_at}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text)' }}>{tip.guest_name}</span>
                <span style={{ fontFamily: font, fontSize: 17, fontWeight: 700, color: '#04A628' }}>{formatEuro(tip.amount)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <HostBottomNav />

      <WelcomeTourSheet
        open={pageTourOpen}
        onClose={handleClosePageTour}
        slides={hostEarningsTourSlides}
        testIdPrefix="host-earnings-page-tour"
      />
    </div>
  );
}